import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Lightbulb, Plus } from 'lucide-react';
import { ideaApi } from '../services/api';
import { useProjectStore } from '../store/useProjectStore';
import RefreshButton from '../components/common/RefreshButton';
import IdeaWizard from '../components/ideation/IdeaWizard';
import IdeaDetailModal from '../components/ideation/IdeaDetailModal';
import { IDEA_STATUS_META } from '../types';
import type { IdeaStatus, WorkItem } from '../types';

// Pipeline column order (left → right).
const COLUMNS: IdeaStatus[] = ['draft', 'submitted', 'in_review', 'approved', 'realized', 'parked', 'rejected'];

export default function IdeationPage() {
  const { projectId } = useParams<{ projectId?: string }>();
  const { projects } = useProjectStore();
  const [ideas, setIdeas] = useState<WorkItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [selected, setSelected] = useState<WorkItem | null>(null);

  const project = projectId ? projects.find((p) => p._id === projectId) : null;

  const load = async () => {
    setLoading(true);
    try {
      const rows = await ideaApi.list(projectId);
      setIdeas(rows || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const byStatus = useMemo(() => {
    const map: Record<string, WorkItem[]> = {};
    for (const c of COLUMNS) map[c] = [];
    for (const i of ideas) {
      const s = (i.ideaStatus || 'draft') as IdeaStatus;
      (map[s] || (map[s] = [])).push(i);
    }
    for (const c of COLUMNS) map[c].sort((a, b) => (b.wsjfScore || 0) - (a.wsjfScore || 0));
    return map;
  }, [ideas]);

  // After a create/change, refresh from source of truth.
  const onChanged = () => load();

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
            <Lightbulb className="text-amber-500" size={20} />
          </div>
          <div>
            <h1 className="text-lg font-semibold">Ideation{project ? ` — ${project.name}` : ''}</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Propose new capabilities. Approved ideas become epics or features scheduled into a PI.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <RefreshButton onRefresh={load} />
          <button
            onClick={() => setWizardOpen(true)}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold bg-accent text-white rounded-lg hover:bg-accent/90"
          >
            <Plus size={16} /> New Idea
          </button>
        </div>
      </div>

      {/* Pipeline */}
      <div className="flex-1 overflow-x-auto overflow-y-hidden p-4">
        {loading ? (
          <div className="flex items-center justify-center h-40 text-gray-400 text-sm">Loading ideas…</div>
        ) : ideas.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center text-gray-400">
            <Lightbulb size={40} className="mb-3 opacity-40" />
            <p className="text-sm">No ideas yet{project ? ' for this project' : ''}.</p>
            <button onClick={() => setWizardOpen(true)} className="mt-3 text-accent text-sm font-semibold hover:underline">
              Propose the first one
            </button>
          </div>
        ) : (
          <div className="flex gap-3 h-full min-w-max pb-2">
            {COLUMNS.map((col) => {
              const meta = IDEA_STATUS_META[col];
              const list = byStatus[col] || [];
              return (
                <div key={col} className="w-72 shrink-0 flex flex-col bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                  <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-200 dark:border-gray-700">
                    <span className={`w-2 h-2 rounded-full ${meta.dot}`} />
                    <span className="text-sm font-semibold">{meta.label}</span>
                    <span className="ml-auto text-xs text-gray-400">{list.length}</span>
                  </div>
                  <div className="flex-1 overflow-y-auto p-2 space-y-2">
                    {list.map((idea) => (
                      <button
                        key={idea._id}
                        onClick={() => setSelected(idea)}
                        className="w-full text-left bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-3 hover:border-accent hover:shadow-sm transition"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <span className="text-sm font-medium line-clamp-2">{idea.title}</span>
                          {idea.wsjfScore > 0 && (
                            <span className="text-[10px] font-bold text-accent shrink-0">{idea.wsjfScore.toFixed(1)}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-2 text-[11px] text-gray-400">
                          {idea.displayId && <span className="font-mono">{idea.displayId}</span>}
                          <span className="capitalize px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800">{idea.ideaLevel || 'epic'}</span>
                          {!projectId && (
                            <span className="truncate">{projects.find((p) => p._id === idea.projectId)?.name || ''}</span>
                          )}
                        </div>
                      </button>
                    ))}
                    {list.length === 0 && <div className="text-center text-[11px] text-gray-300 dark:text-gray-600 py-4">—</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {wizardOpen && (
        <IdeaWizard
          lockedProjectId={projectId}
          onClose={() => setWizardOpen(false)}
          onCreated={() => { setWizardOpen(false); load(); }}
        />
      )}
      {selected && (
        <IdeaDetailModal
          idea={selected}
          onClose={() => setSelected(null)}
          onChanged={(updated) => { setSelected(updated); onChanged(); }}
        />
      )}
    </div>
  );
}
